import { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useExchangeRate } from './ExchangeRateContext';
import { roundUsdPrice } from '../utils/tourAlgorithms';

// Define available languages
export const LANGUAGES = {
  EN: 'en',
  NP: 'np'
};

// Default language
const DEFAULT_LANGUAGE = LANGUAGES.EN;

// Create language context
export const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key,
  currencySymbol: '$',
  formatPrice: (price) => `$${roundUsdPrice(price)}`
});

// Translation resources
const resources = {
  [LANGUAGES.EN]: {
    common: {
      home: 'Home',
      explore: 'Explore',
      about: 'About Us',
      contact: 'Contact',
      login: 'Log In',
      signup: 'Sign Up',
      search: 'Search',
      bookNow: 'Book Now',
      days: 'Days',
      price: 'Price',
      startingFrom: 'Starting from',
      location: 'Location',
      duration: 'Duration',
      activities: 'Activities',
      season: 'Season',
      altitude: 'Altitude',
      departure: 'Departure',
      availableSeats: 'Available Seats',
      tourDescription: 'Tour Description',
      itinerary: 'Itinerary',
      packages: 'Packages',
      faq: 'FAQ',
      reviews: 'Reviews',
      relatedTours: 'Related Tours',
      viewDetails: 'View Details'
    },
    booking: {
      personalInfo: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      country: 'Country',
      address: 'Address',
      participants: 'Participants',
      date: 'Date',
      payment: 'Payment',
      paymentMethod: 'Payment Method',
      creditCard: 'Credit Card',
      paypal: 'PayPal',
      mobileWallet: 'Mobile Wallet',
      eSewa: 'eSewa',
      cardNumber: 'Card Number',
      expiryDate: 'Expiry Date',
      cvv: 'CVV',
      nameOnCard: 'Name on Card',
      termsAgree: 'I agree to the terms and conditions',
      proceedToPayment: 'Proceed to Payment',
      confirmBooking: 'Confirm Booking'
    },
    home: {
      hero: {
        title: 'Explore Nepal on Wheels',
        subtitle: 'Discover the beauty of Nepal through unique cycling tours',
        cta: 'Find Your Adventure'
      },
      featured: {
        title: 'Featured Tours',
        viewAll: 'View All Tours'
      },
      experience: {
        title: 'The Ultimate Experience',
        subtitle: 'Why choose Nepal Tourism Package?'
      },
      testimonials: {
        title: 'What Our Customers Say'
      }
    }
  },
  [LANGUAGES.NP]: {
    common: {
      home: 'गृहपृष्ठ',
      explore: 'अन्वेषण',
      about: 'हाम्रो बारेमा',
      contact: 'सम्पर्क',
      login: 'लग इन',
      signup: 'साइन अप',
      search: 'खोज्नुहोस्',
      bookNow: 'अहिले बुक गर्नुहोस्',
      days: 'दिन',
      price: 'मूल्य',
      startingFrom: 'बाट सुरु',
      location: 'स्थान',
      duration: 'अवधि',
      activities: 'गतिविधिहरू',
      season: 'ऋतु',
      altitude: 'उचाई',
      departure: 'प्रस्थान',
      availableSeats: 'उपलब्ध सिटहरू',
      tourDescription: 'भ्रमण विवरण',
      itinerary: 'भ्रमण कार्यक्रम',
      packages: 'प्याकेजहरू',
      faq: 'प्रश्नोत्तर',
      reviews: 'समीक्षाहरू',
      relatedTours: 'सम्बन्धित भ्रमणहरू',
      viewDetails: 'विवरण हेर्नुहोस्'
    },
    booking: {
      personalInfo: 'व्यक्तिगत जानकारी',
      firstName: 'पहिलो नाम',
      lastName: 'थर',
      email: 'इमेल',
      phone: 'फोन',
      country: 'देश',
      address: 'ठेगाना',
      participants: 'सहभागीहरू',
      date: 'मिति',
      payment: 'भुक्तानी',
      paymentMethod: 'भुक्तानी विधि',
      creditCard: 'क्रेडिट कार्ड',
      paypal: 'पेपाल',
      mobileWallet: 'मोबाइल वालेट',
      eSewa: 'ईसेवा',
      cardNumber: 'कार्ड नम्बर',
      expiryDate: 'समाप्ति मिति',
      cvv: 'सिभिभि',
      nameOnCard: 'कार्डमा नाम',
      termsAgree: 'म नियम र शर्तहरूसँग सहमत छु',
      proceedToPayment: 'भुक्तानीमा जानुहोस्',
      confirmBooking: 'बुकिङ्ग पुष्टि गर्नुहोस्'
    },
    home: {
      hero: {
        title: 'पाङ्ग्रामा नेपालको अन्वेषण गर्नुहोस्',
        subtitle: 'अनौठो साइकल भ्रमणहरू मार्फत नेपालको सुन्दरता पत्ता लगाउनुहोस्',
        cta: 'आफ्नो साहसिक यात्रा खोज्नुहोस्'
      },
      featured: {
        title: 'विशेष भ्रमणहरू',
        viewAll: 'सबै भ्रमणहरू हेर्नुहोस्'
      },
      experience: {
        title: 'अल्टिमेट अनुभव',
        subtitle: 'किन नेभिगेट अन नेभिगेट अन ह्विल्स चयन गर्ने?'
      },
      testimonials: {
        title: 'हाम्रा ग्राहकहरू के भन्छन्'
      }
    }
  }
};

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  const { rate, usdToNpr } = useExchangeRate();
  // Try to get stored language or use browser settings
  const getBrowserLanguage = () => {
    // If user previously selected a language, use that
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage && Object.values(LANGUAGES).includes(storedLanguage)) {
      return storedLanguage;
    }
    
    // Otherwise try to detect from browser
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'ne') return LANGUAGES.NP; // Nepali
    
    return DEFAULT_LANGUAGE;
  };

  const [language, setLanguageState] = useState(getBrowserLanguage());

  // Set language and save to local storage
  const setLanguage = (lang) => {
    if (Object.values(LANGUAGES).includes(lang)) {
      localStorage.setItem('language', lang);
      setLanguageState(lang);
      
      // Update HTML lang attribute for accessibility
      document.documentElement.lang = lang;
      
      // Update body direction for RTL languages if needed
      // document.body.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
  };

  // Translation function
  const t = (key, params = {}) => {
    // Split key by dot notation (e.g. 'common.button.submit')
    const keys = key.split('.');
    
    // Get nested value from resources
    let translation = resources[language];
    for (const k of keys) {
      translation = translation?.[k];
      if (!translation) break;
    }
    
    // Return the key itself if translation not found
    if (!translation) return key;
    
    // Replace params in translation string
    if (params && Object.keys(params).length) {
      return Object.keys(params).reduce(
        (str, param) => str.replace(`{{${param}}}`, params[param]),
        translation
      );
    }
    
    return translation;
  };

  // Set language on first render
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  
  const currencySymbol = language === LANGUAGES.NP ? 'रू' : '$';
  const formatPrice = language === LANGUAGES.NP
    ? (price) => `रू ${usdToNpr(price).toLocaleString('en-NP')}`
    : (price) => `$${roundUsdPrice(price).toLocaleString('en-US')}`;

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t,
      currencySymbol,
      formatPrice,
      exchangeRate: rate
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired
};
